/**
 * MFA client — `/api/auth/mfa/*` (#597 Phase 3).
 *
 * BFF pattern: the SPA never holds a Cognito access token. Every
 * MFA operation goes through FlowStack's backend, which resolves the
 * caller's Cognito token from the session (Phase 1) and calls the
 * ``cognito-idp`` self-API on their behalf (Phase 2).
 *
 * For the WebAuthn ceremony, the browser MUST call
 * ``navigator.credentials.create()`` locally between the "begin" and
 * "complete" round-trips — that's the step the user's Touch ID /
 * YubiKey physically interacts with, and it cannot be proxied. The
 * ``credential_creation_options`` blob we receive from ``/passkey/begin``
 * is base64url-encoded (Cognito's SDK shape); the SPA converts the
 * binary fields to ArrayBuffers before handing to WebAuthn, and back
 * to base64url before POSTing the attestation to ``/passkey/complete``.
 * Base64url helpers live in :mod:`@/lib/webauthn`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";
import {
  arrayBufferToBase64Url,
  base64UrlToArrayBuffer,
  type CognitoWebAuthnAttestation,
  type CognitoWebAuthnOptions,
} from "@/lib/webauthn";

// --- Wire types (mirror the FastAPI response shapes) --------------------

export interface MFAFactors {
  totp_enabled: boolean;
  preferred_mfa: string | null; // "TOTP" or null
  passkey_count: number;
}

export interface TOTPAssociateResponse {
  secret_code: string;
  otpauth_uri: string;
}

export interface Passkey {
  credential_id: string;
  name: string | null;
  created_at: string | null;
  attachment: string | null; // "platform" | "cross-platform" | null
}

// --- Query keys ---------------------------------------------------------

const MFA_FACTORS_KEY = ["auth", "mfa", "factors"] as const;
const PASSKEYS_KEY = ["auth", "mfa", "passkeys"] as const;

// --- Reads --------------------------------------------------------------

/**
 * Snapshot of the caller's currently-enrolled MFA factors.
 *
 * Feeds the "Authenticator app: enrolled / not enrolled" copy and the
 * "You have N passkeys" summary at the top of the Security section.
 * 60s staleTime matches ``useTrustedBrowser`` — MFA state changes on
 * user action (enroll / disable / delete), and those mutations
 * invalidate this query directly, so the polling interval is a safety
 * net rather than the primary source of freshness.
 */
export function useMFAFactors() {
  return useQuery({
    queryKey: MFA_FACTORS_KEY,
    queryFn: () => apiFetch<MFAFactors>("/api/auth/mfa/factors"),
    staleTime: 60_000,
  });
}

/**
 * The caller's registered passkeys.
 *
 * The dedicated endpoint (unlike the ``passkey_count`` in
 * ``useMFAFactors``) does NOT soft-fail — a Cognito rejection surfaces
 * as an error the UI can render, so a user isn't quietly told they
 * have zero passkeys when the real problem is a stale access token.
 */
export function usePasskeys() {
  return useQuery({
    queryKey: PASSKEYS_KEY,
    queryFn: async () => {
      const resp = await apiFetch<{ passkeys: Passkey[] }>("/api/auth/mfa/passkeys");
      return resp.passkeys;
    },
    staleTime: 30_000,
  });
}

// --- TOTP flow ----------------------------------------------------------

/**
 * Mint a fresh TOTP secret + return the QR-encodable otpauth URI.
 *
 * Cognito emits a fresh secret on every call (does NOT rotate an
 * existing partial enrollment) so the UI should NOT re-invoke this
 * on retry — the second QR is live and the first is dead. The
 * dialog owns "one call per enrollment attempt" discipline.
 */
export function useTOTPBegin() {
  return useMutation({
    mutationFn: () =>
      apiFetch<TOTPAssociateResponse>("/api/auth/mfa/totp/begin", { method: "POST" }),
  });
}

/**
 * Verify the 6-digit code + flip TOTP to enabled + preferred.
 *
 * On success, invalidate the factors snapshot so the Security section
 * re-renders the "TOTP: enrolled" state without a manual refresh.
 * On error, do NOT invalidate — a network/response-lost mutation
 * still might have succeeded server-side, but the ``otp_mismatch``
 * / ``otp_expired`` failures are deterministic client-input errors,
 * so re-asking the server does not help.
 */
export function useTOTPComplete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; device_name?: string }) =>
      apiFetch<{ ok: true }>("/api/auth/mfa/totp/complete", {
        method: "POST",
        body: JSON.stringify(input),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MFA_FACTORS_KEY });
    },
  });
}

export function useTOTPDisable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: true }>("/api/auth/mfa/totp", { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MFA_FACTORS_KEY });
    },
    onError: () => {
      // Same discipline as useForgetTrustedBrowser: a security-adjacent
      // DELETE may have succeeded even if the response was lost;
      // invalidate rather than assume cache is correct.
      void queryClient.invalidateQueries({ queryKey: MFA_FACTORS_KEY });
    },
  });
}

// --- Passkey (WebAuthn) flow -------------------------------------------

/**
 * Full passkey-registration ceremony: begin → browser create → complete.
 *
 * The three steps are wrapped in one mutation so the caller can render
 * a single "Add passkey" button that owns the entire flow's loading /
 * error surface. Failure modes:
 *
 * - Backend rejected begin (e.g. rate-limited, WebAuthn not enabled) —
 *   the standard 4xx error body reaches the caller.
 * - Browser rejected the create call (user cancelled the Touch ID
 *   prompt, no authenticator available, timeout) — surface as an
 *   error with a WebAuthn-flavored message.
 * - Backend rejected complete (bad attestation, credential collision) —
 *   the standard 4xx error body reaches the caller.
 *
 * On success, invalidate BOTH the passkey list and the factors count.
 */
export function useAddPasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Step 1 — server-side ceremony start.
      const begin = await apiFetch<{ credential_creation_options: CognitoWebAuthnOptions }>(
        "/api/auth/mfa/passkey/begin",
        { method: "POST" }
      );
      // Step 2 — browser-side ceremony (the physical Touch ID / key
      // interaction). Convert Cognito's base64url fields to
      // ArrayBuffers for the standard WebAuthn call, then convert the
      // browser's binary response back to base64url for the wire.
      const publicKey = decodeCreationOptions(begin.credential_creation_options);
      const credential = await navigator.credentials.create({ publicKey });
      if (!credential || credential.type !== "public-key") {
        throw new Error("WebAuthn registration did not produce a public-key credential");
      }
      const attestation = encodeAttestation(credential as PublicKeyCredential);
      // Step 3 — server-side ceremony complete.
      return apiFetch<{ ok: true }>("/api/auth/mfa/passkey/complete", {
        method: "POST",
        body: JSON.stringify({ credential: attestation }),
        headers: { "Content-Type": "application/json" },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PASSKEYS_KEY });
      void queryClient.invalidateQueries({ queryKey: MFA_FACTORS_KEY });
    },
  });
}

export function useDeletePasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentialId: string) =>
      apiFetch<{ ok: true }>(`/api/auth/mfa/passkey/${encodeURIComponent(credentialId)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PASSKEYS_KEY });
      void queryClient.invalidateQueries({ queryKey: MFA_FACTORS_KEY });
    },
    onError: () => {
      // Same as TOTP disable — invalidate on error so the UI reflects
      // the true server state, not the assumed cache state.
      void queryClient.invalidateQueries({ queryKey: PASSKEYS_KEY });
      void queryClient.invalidateQueries({ queryKey: MFA_FACTORS_KEY });
    },
  });
}

// --- WebAuthn coding helpers -------------------------------------------
//
// Cognito ships ``PublicKeyCredentialCreationOptions`` with binary
// fields (``challenge``, ``user.id``, ``excludeCredentials[i].id``)
// as base64url strings. The WebAuthn API needs ArrayBuffers. Similarly,
// the browser's ``AuthenticatorAttestationResponse`` carries binary
// fields we need to base64url before sending back to Cognito.

function decodeCreationOptions(opts: CognitoWebAuthnOptions): PublicKeyCredentialCreationOptions {
  const decoded: PublicKeyCredentialCreationOptions = {
    ...(opts as unknown as PublicKeyCredentialCreationOptions),
    challenge: base64UrlToArrayBuffer(opts.challenge),
    user: {
      ...opts.user,
      id: base64UrlToArrayBuffer(opts.user.id),
    },
  };
  if (opts.excludeCredentials) {
    decoded.excludeCredentials = opts.excludeCredentials.map((c) => ({
      ...c,
      id: base64UrlToArrayBuffer(c.id),
    }));
  }
  return decoded;
}

function encodeAttestation(credential: PublicKeyCredential): CognitoWebAuthnAttestation {
  const resp = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? null,
    response: {
      clientDataJSON: arrayBufferToBase64Url(resp.clientDataJSON),
      attestationObject: arrayBufferToBase64Url(resp.attestationObject),
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}
